/* ============================================================
   stain-samples.js — REDESIGN
   ------------------------------------------------------------
   Premium minimal stain picker matching the reference:
     • Editorial header
     • Filter row (search / species pills / dropdown)
     • Image-as-card stain grid with elegant zoom button
     • Soft green selection state (no big check overlay)
     • Floating "Review Samples" FAB (visible when ≥1 selected)
     • Right slide-in drawer with progress, items, checkout

   Checkout still splits free vs paid into different variants:
     - First N selections (N = FREE_LIMIT) → FREE_VARIANT_ID
     - Beyond that → PAID_VARIANT_ID

   Each sample is added as its own cart line with visible
   line item properties (Stain / Wood Species / Sample), plus a
   backup cart attribute ("Stain Samples") and order note.

   URL-aware filtering:
     - ?wood=cherry  → opens to Cherry tab
     - ?wood=all     → shows everything
     - clicking tabs updates URL silently (no reload)
     - back/forward buttons restore the previous wood
   ============================================================ */
(function () {
  'use strict';

  const root = document.querySelector('[data-stain-samples]');
  if (!root) return;

  const SAMPLE_PRICE = parseFloat(root.dataset.samplePrice) || 6;
  const FREE_LIMIT = parseInt(root.dataset.freeLimit, 10) || 3;
  const CURRENCY = root.dataset.currency || 'USD';
  const FREE_VARIANT_ID = root.dataset.freeVariantId || '';
  const PAID_VARIANT_ID = root.dataset.paidVariantId || '';

  let STAINS = [];
  try {
    const jsonEl = document.querySelector('[data-stains-json]');
    STAINS = JSON.parse(jsonEl.textContent);
  } catch (err) {
    console.error('[stain-samples] Failed to parse stain data', err);
    return;
  }

  const gridEl = root.querySelector('[data-stain-grid]');
  if (!gridEl) return;

  /* ----- helpers ----- */
  const fmtMoney = (cents) => {
    const value = cents / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: CURRENCY }).format(value);
    } catch {
      return '$' + value.toFixed(2);
    }
  };
  const escapeHtml = (s) =>
    String(s ?? '')
      .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;').replaceAll("'", '&#39;');
  const escapeAttr = (s) => String(s ?? '').replaceAll('"', '&quot;');

  const emptyMarkup = (msg) => `
    <div class="ss-empty">
      <div class="ss-empty__ico" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="h5" style="color: var(--ss-fg);">${msg}</p>
    </div>`;

  if (!STAINS.length) {
    gridEl.innerHTML = `
      <div class="ss-empty">
        <div class="ss-empty__ico" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </div>
        <p class="h5" style="color: var(--ss-fg);">No stains added yet</p>
        <p class="caption-with-letter-spacing">In the Theme Editor, click "Add block" → "Stain" to add stain photos.</p>
      </div>`;
    return;
  }

  const SPECIES_LIST = [...new Set(STAINS.map(s => s.species))];

  /* Default landing wood. If "Brown Maple" exists in the data, use it.
     Otherwise fall back to whichever wood is first. This way the section
     opens to a familiar starting point regardless of block order. */
  const DEFAULT_SPECIES = SPECIES_LIST.includes('Brown Maple')
    ? 'Brown Maple'
    : SPECIES_LIST[0];

  const state = {
    activeSpecies: DEFAULT_SPECIES, // null = All Wood Species
    search: '',
    selected: new Set(),
  };

  /* ============================================================
     URL SYNC — shareable wood selection
     ------------------------------------------------------------
     Reads ?wood=<slug> on page load so links like
     /pages/stains?wood=cherry open directly to that wood.
     Updates the URL when the user clicks a tab (no reload —
     uses history.pushState). Listens to back/forward so the
     browser's nav buttons work naturally.
     ============================================================ */
  function slugify(s) {
    return String(s || '').toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  }
  function findSpeciesBySlug(slug) {
    if (!slug) return undefined;
    return SPECIES_LIST.find(sp => slugify(sp) === slug);
  }
  function readWoodFromURL() {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get('wood');
    if (slug === 'all') return null;
    const match = findSpeciesBySlug(slug);
    return match || undefined; // undefined = no override; null = "All"
  }
  function updateURL(species, { replace = false } = {}) {
    const params = new URLSearchParams(window.location.search);
    if (species === null) {
      params.set('wood', 'all');
    } else if (species) {
      params.set('wood', slugify(species));
    } else {
      params.delete('wood');
    }
    const qs = params.toString();
    const url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    if (replace) history.replaceState({ wood: species }, '', url);
    else history.pushState({ wood: species }, '', url);
  }
  // Honor an incoming ?wood= on first load
  const initialWood = readWoodFromURL();
  if (initialWood !== undefined) {
    state.activeSpecies = initialWood;
  }
  // Re-filter on back/forward
  window.addEventListener('popstate', () => {
    const w = readWoodFromURL();
    state.activeSpecies = (w === undefined) ? DEFAULT_SPECIES : w;
    renderTabs();
    renderGrid();
    if (speciesDropdown) {
      speciesDropdown.value = state.activeSpecies === null ? '__all__' : state.activeSpecies;
    }
  });

  function currentFilteredStains() {
    const q = state.search.toLowerCase().trim();
    return STAINS.filter(s => {
      if (state.activeSpecies !== null && s.species !== state.activeSpecies) return false;
      if (q && !s.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }

  /* ============================================================
     TABS (species pills)
     ============================================================ */
  const tabsEl = root.querySelector('[data-species-tabs]');
  function renderTabs() {
    if (!tabsEl) return;
    tabsEl.innerHTML = SPECIES_LIST.map(sp => {
      const active = sp === state.activeSpecies ? 'is-active' : '';
      return `<button class="ss-tab h6 ${active}" data-species="${escapeAttr(sp)}" role="tab" aria-selected="${sp === state.activeSpecies}" type="button">
        ${escapeHtml(sp)}
      </button>`;
    }).join('');
    tabsEl.querySelectorAll('.ss-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        state.activeSpecies = btn.dataset.species;
        updateURL(state.activeSpecies);
        renderTabs();
        renderGrid();
        btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
    });
  }

  /* ============================================================
     DROPDOWN (right side)
     ============================================================ */
  /* The dropdown is a secondary way to switch species. It defaults to
     "All Wood Species" (a virtual option that maps to null filtering)
     so when there are many species the user can still see the full
     "everything" view without scrolling pills. */
  const speciesDropdown = root.querySelector('[data-species-dropdown]');
  if (speciesDropdown) {
    const optionsHtml = ['<option value="__all__">All Wood Species</option>']
      .concat(SPECIES_LIST.map(sp => `<option value="${escapeAttr(sp)}">${escapeHtml(sp)}</option>`))
      .join('');
    speciesDropdown.innerHTML = optionsHtml;
    /* Dropdown defaults to "All Wood Species" — it's a secondary
       "show me everything" affordance. But if the URL has an explicit
       ?wood= param, reflect that in the dropdown too so URL state
       and dropdown stay in sync. */
    if (initialWood !== undefined) {
      speciesDropdown.value = initialWood === null ? '__all__' : initialWood;
    } else {
      speciesDropdown.value = '__all__';
    }
    speciesDropdown.addEventListener('change', (e) => {
      const v = e.target.value;
      if (v === '__all__') {
        state.activeSpecies = null;
      } else {
        state.activeSpecies = v;
      }
      updateURL(state.activeSpecies);
      renderTabs();
      renderGrid();
    });
  }

  /* ============================================================
     GRID
     ============================================================ */
  function renderGrid() {
    const filtered = currentFilteredStains();
    if (filtered.length === 0) {
      gridEl.innerHTML = emptyMarkup(
        state.activeSpecies
          ? `No stains match your search in ${escapeHtml(state.activeSpecies)}.`
          : `No stains match your search.`
      );
      return;
    }
    gridEl.innerHTML = filtered.map(s => {
      const sel = state.selected.has(String(s.id));
      const imgTag = s.image
        ? `<img src="${escapeAttr(s.image)}" alt="${escapeAttr(s.name)} stain on ${escapeAttr(s.species)}" loading="lazy" class="ss-card__img" />`
        : `<div class="ss-card__noimg"><span class="caption-with-letter-spacing">No photo</span></div>`;
      return `
      <article class="ss-card ${sel ? 'is-selected' : ''}" data-id="${escapeAttr(s.id)}" tabindex="0" role="button" aria-pressed="${sel}" aria-label="${escapeAttr(s.name)} stain on ${escapeAttr(s.species)}">
        ${imgTag}
        ${s.popular ? '<div class="ss-card__badge" aria-label="Popular stain" title="Popular stain"></div>' : ''}
        <button class="ss-zoom-btn" type="button" data-zoom aria-label="View larger photo of ${escapeAttr(s.name)}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </button>
        <div class="ss-card__label">
          <p class="ss-card__name h6">${escapeHtml(s.name)}</p>
          <p class="ss-card__species caption-with-letter-spacing">${escapeHtml(s.species)}</p>
        </div>
      </article>`;
    }).join('');

    gridEl.querySelectorAll('.ss-card').forEach(card => {
      const id = card.dataset.id;
      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-zoom]')) return;
        toggleSelect(id);
      });
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleSelect(id);
        }
        if (e.key === 'z' || e.key === 'Z') {
          e.preventDefault();
          openLightbox(id);
        }
      });
      const zoomBtn = card.querySelector('[data-zoom]');
      if (zoomBtn) zoomBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        openLightbox(id);
      });
    });
  }

  function toggleSelect(id) {
    id = String(id);
    const wasSelected = state.selected.has(id);
    if (wasSelected) state.selected.delete(id);
    else state.selected.add(id);
    renderAll();
    if (!wasSelected) bumpFab();
    if (lightboxState.open && String(lightboxState.currentId) === id) updateLightboxActionState();
  }
  function removeSelect(id) {
    state.selected.delete(String(id));
    renderAll();
  }
  function renderAll() {
    renderGrid();
    renderFab();
    renderDrawer();
    renderCompare();
  }

  /* ============================================================
     SEARCH
     ============================================================ */
  const searchInput = root.querySelector('[data-search]');
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      state.search = e.target.value;
      renderGrid();
    });
  }

  /* ============================================================
     FAB CLUSTER (Compare + Review Samples)
     ------------------------------------------------------------
     Both pills share a single cluster container that fades in
     when the user has any selection. Compare scrolls to the
     inline section; Review opens the drawer.
     ============================================================ */
  const fabCluster = root.querySelector('[data-fab-cluster]');
  const fab = root.querySelector('[data-fab]');
  const fabCount = root.querySelector('[data-fab-count]');
  const compareFab = root.querySelector('[data-compare-fab]');
  const compareFabCount = root.querySelector('[data-compare-fab-count]');

  function renderFab() {
    const total = state.selected.size;
    if (total > 0) {
      if (fabCluster) {
        fabCluster.classList.add('is-visible');
        fabCluster.setAttribute('aria-hidden', 'false');
      }
      if (fabCount) fabCount.textContent = total;
      if (compareFabCount) compareFabCount.textContent = total;
    } else {
      if (fabCluster) {
        fabCluster.classList.remove('is-visible');
        fabCluster.setAttribute('aria-hidden', 'true');
      }
    }
  }
  function bumpFab() {
    if (!fab) return;
    fab.classList.remove('is-bump');
    // Force reflow to restart animation
    // eslint-disable-next-line no-unused-expressions
    void fab.offsetWidth;
    fab.classList.add('is-bump');
  }
  if (fab) {
    fab.addEventListener('click', openDrawer);
    fab.addEventListener('animationend', () => fab.classList.remove('is-bump'));
  }
  /* Compare button — smooth-scrolls to the inline comparison section
     so the user can see their picks at full size. If the comparison
     section is disabled in settings, fall back to scrolling to the
     bottom of the page. */
  if (compareFab) {
    compareFab.addEventListener('click', () => {
      const target = root.querySelector('[data-compare]');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Compare section disabled — scroll to bottom of page as a graceful fallback
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
      }
    });
  }

  /* ============================================================
     DRAWER (right slide-in)
     ============================================================ */
  const drawer = root.querySelector('[data-drawer]');
  const drawerBackdrop = root.querySelector('[data-drawer-backdrop]');
  const drawerClose = root.querySelector('[data-drawer-close]');
  const drawerList = root.querySelector('[data-drawer-list]');
  const drawerProgress = root.querySelector('[data-drawer-progress]');
  const drawerProgressLabel = root.querySelector('[data-drawer-progress-label]');
  const drawerCount = root.querySelector('[data-drawer-count]');
  const drawerSamplesVal = root.querySelector('[data-drawer-samples-val]');
  const drawerShippingVal = root.querySelector('[data-drawer-shipping-val]');
  const drawerTotalVal = root.querySelector('[data-drawer-total-val]');
  const drawerPaidRow = root.querySelector('[data-drawer-paid-row]');
  const drawerPaidLabel = root.querySelector('[data-drawer-paid-label]');
  const drawerPaidValue = root.querySelector('[data-drawer-paid-value]');

  let drawerOpen = false;

  function openDrawer() {
    if (!drawer) return;
    drawerOpen = true;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (drawerBackdrop) drawerBackdrop.classList.add('is-open');
    document.body.classList.add('ss-drawer-open');
    // Focus first focusable for a11y
    setTimeout(() => {
      if (drawerClose) drawerClose.focus();
    }, 100);
  }
  function closeDrawer() {
    if (!drawer) return;
    drawerOpen = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (drawerBackdrop) drawerBackdrop.classList.remove('is-open');
    document.body.classList.remove('ss-drawer-open');
  }
  if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
  if (drawerBackdrop) drawerBackdrop.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawerOpen && !lightboxState.open) closeDrawer();
  });

  function renderDrawer() {
    if (!drawer) return;
    const total = state.selected.size;
    const freeUsed = Math.min(total, FREE_LIMIT);
    const paid = Math.max(0, total - FREE_LIMIT);
    const remaining = Math.max(0, FREE_LIMIT - total);
    const priceCents = paid * SAMPLE_PRICE * 100;

    // Header count: "3 of 3 free"
    if (drawerCount) drawerCount.textContent = `${freeUsed} of ${FREE_LIMIT} free`;

    // Progress segments
    if (drawerProgress) {
      const segs = [];
      for (let i = 0; i < FREE_LIMIT; i++) {
        segs.push(`<span class="ss-drawer__progress-seg ${i < freeUsed ? 'is-filled' : ''}"></span>`);
      }
      drawerProgress.innerHTML = segs.join('');
    }

    // Progress label
    if (drawerProgressLabel) {
      let txt;
      if (total === 0) txt = `${FREE_LIMIT} FREE SAMPLES AVAILABLE`;
      else if (remaining > 0) txt = `${remaining} FREE SAMPLE${remaining === 1 ? '' : 'S'} REMAINING`;
      else if (paid === 0) txt = `ALL ${FREE_LIMIT} FREE SAMPLES USED`;
      else txt = `${paid} ADDITIONAL · ${fmtMoney(SAMPLE_PRICE * 100)} EACH`;
      drawerProgressLabel.textContent = txt;
    }

    // List
    if (drawerList) {
      const selectedArr = [...state.selected];
      if (selectedArr.length === 0) {
        drawerList.innerHTML = `
          <div class="ss-drawer__empty">
            <div class="ss-drawer__empty-ico" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><polyline points="9 11 12 14 22 4"/></svg>
            </div>
            <p class="ss-drawer__empty-title h6">No samples selected yet</p>
            <p class="caption-with-letter-spacing">Pick stains from the grid to add them here.</p>
          </div>`;
      } else {
        drawerList.innerHTML = selectedArr.map(id => {
          const s = STAINS.find(x => String(x.id) === id);
          if (!s) return '';
          const thumb = s.image
            ? `<div class="ss-drawer__item-thumb"><img src="${escapeAttr(s.image)}" alt="" /></div>`
            : `<div class="ss-drawer__item-thumb"></div>`;
          return `
            <div class="ss-drawer__item">
              ${thumb}
              <div class="ss-drawer__item-info">
                <p class="ss-drawer__item-name">${escapeHtml(s.name)}</p>
                <p class="ss-drawer__item-species caption-with-letter-spacing">${escapeHtml(s.species)}</p>
              </div>
              <button class="ss-drawer__item-remove" type="button" data-drawer-remove="${escapeAttr(s.id)}" aria-label="Remove ${escapeAttr(s.name)}">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>`;
        }).join('');
        drawerList.querySelectorAll('[data-drawer-remove]').forEach(btn => {
          btn.addEventListener('click', () => removeSelect(btn.dataset.drawerRemove));
        });
      }
    }

    // Pricing
    if (drawerSamplesVal) {
      drawerSamplesVal.textContent = 'FREE';
      drawerSamplesVal.classList.add('ss-drawer__line-val');
      drawerSamplesVal.classList.remove('ss-drawer__line-val--neutral');
    }
    if (drawerShippingVal) drawerShippingVal.textContent = 'FREE';

    if (drawerPaidRow && drawerPaidLabel && drawerPaidValue) {
      if (paid > 0) {
        drawerPaidRow.style.display = '';
        drawerPaidLabel.textContent = `${paid} additional × ${fmtMoney(SAMPLE_PRICE * 100)}`;
        drawerPaidValue.textContent = fmtMoney(priceCents);
      } else {
        drawerPaidRow.style.display = 'none';
      }
    }

    if (drawerTotalVal) {
      drawerTotalVal.textContent = priceCents === 0 ? '$0.00' : fmtMoney(priceCents);
    }
  }

  /* ============================================================
     SIDE-BY-SIDE COMPARISON
     ============================================================ */
  const compareEl = root.querySelector('[data-compare]');
  const compareStrip = root.querySelector('[data-compare-strip]');
  const compareLabels = root.querySelector('[data-compare-labels]');

  function renderCompare() {
  if (!compareEl || !compareStrip || !compareLabels) return;
  const selectedArr = [...state.selected];
  /* Section is always visible so the feature is discoverable.
     We swap content between three states:
       0 selected  → friendly empty state inviting picks
       1 selected  → "pick one more" hint (still useful context)
       2+ selected → the real side-by-side strip */
  compareEl.classList.add('is-visible');

  if (selectedArr.length === 0) {
    compareEl.classList.add('is-empty');
    compareEl.classList.remove('is-single');
    compareStrip.innerHTML = `
      <div class="ss-compare__empty">
        <div class="ss-compare__empty-ico" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
        </div>
        <p class="ss-compare__empty-text">Pick two or more stains above to see them side by side here.</p>
      </div>`;
    compareLabels.innerHTML = '';
    return;
  }

  if (selectedArr.length === 1) {
    compareEl.classList.remove('is-empty');
    compareEl.classList.add('is-single');
    const s = STAINS.find(x => String(x.id) === selectedArr[0]);
    const inner = s && s.image
      ? `<img class="ss-compare__band-img" src="${escapeAttr(s.image)}" alt="${escapeAttr(s.name)} stain on ${escapeAttr(s.species)}" loading="lazy" />`
      : `<div class="ss-compare__band-noimg" aria-hidden="true"></div>`;
    compareStrip.innerHTML = `
      <div class="ss-compare__band ss-compare__band--single" data-compare-band="${escapeAttr(s.id)}" title="${escapeAttr(s.name)} — ${escapeAttr(s.species)}">
        ${inner}
        ${s.popular ? '<div class="ss-compare__badge" aria-label="Popular stain" title="Popular stain"></div>' : ''}
      </div>
      <div class="ss-compare__hint">
        <p class="ss-compare__hint-text">Pick one more stain to compare.</p>
      </div>`;
    compareLabels.innerHTML = `<div class="ss-compare__label">
      <p class="ss-compare__label-name h6">${escapeHtml(s.name)}</p>
      <p class="ss-compare__label-species caption-with-letter-spacing">${escapeHtml(s.species)}</p>
    </div>`;
    compareStrip.querySelectorAll('[data-compare-band]').forEach(band => {
      band.addEventListener('click', () => openLightbox(band.dataset.compareBand));
    });
    return;
  }

  /* 2+ selected — full side-by-side */
  compareEl.classList.remove('is-empty', 'is-single');
  compareStrip.innerHTML = selectedArr.map(id => {
    const s = STAINS.find(x => String(x.id) === id);
    if (!s) return '';
    const inner = s.image
      ? `<img class="ss-compare__band-img" src="${escapeAttr(s.image)}" alt="${escapeAttr(s.name)} stain on ${escapeAttr(s.species)}" loading="lazy" />`
      : `<div class="ss-compare__band-noimg" aria-hidden="true"></div>`;
    return `<div class="ss-compare__band" data-compare-band="${escapeAttr(s.id)}" title="${escapeAttr(s.name)} — ${escapeAttr(s.species)}">${inner}${s.popular ? '<div class="ss-compare__badge" aria-label="Popular stain" title="Popular stain"></div>' : ''}</div>`;
  }).join('');

  compareLabels.innerHTML = selectedArr.map(id => {
    const s = STAINS.find(x => String(x.id) === id);
    if (!s) return '';
    return `<div class="ss-compare__label">
      <p class="ss-compare__label-name h6">${escapeHtml(s.name)}</p>
      <p class="ss-compare__label-species caption-with-letter-spacing">${escapeHtml(s.species)}</p>
    </div>`;
  }).join('');

  compareStrip.querySelectorAll('[data-compare-band]').forEach(band => {
    band.addEventListener('click', () => openLightbox(band.dataset.compareBand));
  });
}

  /* ============================================================
     LIGHTBOX (preserved from prior implementation)
     ============================================================ */
  const lightbox = root.querySelector('[data-lightbox]');
  const lbImg = lightbox?.querySelector('[data-lb-img]');
  const lbStage = lightbox?.querySelector('[data-lb-stage]');
  const lbTitle = lightbox?.querySelector('[data-lb-title]');
  const lbSubtitle = lightbox?.querySelector('[data-lb-subtitle]');
  const lbAction = lightbox?.querySelector('[data-lb-action]');
  const lbActionLabel = lightbox?.querySelector('[data-lb-action-label]');
  const lbZoomIn = lightbox?.querySelector('[data-lb-zoom-in]');
  const lbZoomOut = lightbox?.querySelector('[data-lb-zoom-out]');
  const lbZoomReset = lightbox?.querySelector('[data-lb-zoom-reset]');
  const lbZoomLevel = lightbox?.querySelector('[data-lb-zoom-level]');
  const lbClose = lightbox?.querySelector('[data-lb-close]');
  const lbPrev = lightbox?.querySelector('[data-lb-prev]');
  const lbNext = lightbox?.querySelector('[data-lb-next]');

  const ZOOM_MIN = 1, ZOOM_MAX = 5, ZOOM_STEP = 0.5;
  const lightboxState = {
    open: false, currentId: null,
    scale: 1, tx: 0, ty: 0,
    isPanning: false, panStartX: 0, panStartY: 0, panOriginTx: 0, panOriginTy: 0,
    pinchStartDist: 0, pinchStartScale: 1,
    swipeStartX: 0, swipeStartY: 0, swipeActive: false,
  };

  function openLightbox(id) {
    if (!lightbox || !lbImg) return;
    const s = STAINS.find(x => String(x.id) === String(id));
    if (!s || !s.image) return;
    lightboxState.open = true;
    lightboxState.currentId = String(id);
    resetZoom();
    lbImg.src = s.image;
    lbImg.alt = `${s.name} stain on ${s.species}`;
    if (lbTitle) lbTitle.textContent = s.name;
    if (lbSubtitle) lbSubtitle.textContent = s.species;
    updateLightboxActionState();
    updateNavButtons();
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ss-lightbox-open');
  }
  function closeLightbox() {
    if (!lightbox) return;
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ss-lightbox-open');
    lightboxState.open = false;
    lightboxState.currentId = null;
    resetZoom();
  }
  function navigateLightbox(direction) {
    const list = currentFilteredStains();
    if (!list.length) return;
    let idx = list.findIndex(s => String(s.id) === lightboxState.currentId);
    if (idx === -1) return;
    idx = (idx + direction + list.length) % list.length;
    openLightbox(list[idx].id);
  }
  function updateNavButtons() {
    const list = currentFilteredStains();
    const show = list.length > 1;
    if (lbPrev) lbPrev.style.display = show ? '' : 'none';
    if (lbNext) lbNext.style.display = show ? '' : 'none';
  }
  function updateLightboxActionState() {
    if (!lbAction || !lbActionLabel) return;
    const isSel = state.selected.has(String(lightboxState.currentId));
    if (isSel) {
      lbActionLabel.textContent = 'Selected — Remove';
      lbAction.classList.add('ss-lightbox__action--selected');
    } else {
      lbActionLabel.textContent = 'Select this sample';
      lbAction.classList.remove('ss-lightbox__action--selected');
    }
  }
  function applyZoom() {
    if (!lbImg) return;
    lbImg.style.transform = `translate(${lightboxState.tx}px, ${lightboxState.ty}px) scale(${lightboxState.scale})`;
    if (lbZoomLevel) lbZoomLevel.textContent = `${Math.round(lightboxState.scale * 100)}%`;
    if (lbZoomIn) lbZoomIn.disabled = lightboxState.scale >= ZOOM_MAX;
    if (lbZoomOut) lbZoomOut.disabled = lightboxState.scale <= ZOOM_MIN;
    if (lightbox) {
      if (lightboxState.scale > 1) lightbox.classList.add('is-zoomed');
      else lightbox.classList.remove('is-zoomed');
    }
  }
  function resetZoom() {
    lightboxState.scale = 1; lightboxState.tx = 0; lightboxState.ty = 0;
    applyZoom();
  }
  function zoomBy(delta, originX, originY) {
    const oldScale = lightboxState.scale;
    const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, oldScale + delta));
    if (newScale === oldScale) return;
    if (originX != null && originY != null && lbImg) {
      const rect = lbImg.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const offsetX = originX - cx;
      const offsetY = originY - cy;
      const ratio = newScale / oldScale;
      lightboxState.tx = lightboxState.tx * ratio + offsetX * (1 - ratio);
      lightboxState.ty = lightboxState.ty * ratio + offsetY * (1 - ratio);
    }
    lightboxState.scale = newScale;
    if (newScale === 1) { lightboxState.tx = 0; lightboxState.ty = 0; }
    applyZoom();
  }
  function constrainPan() {
    if (!lbImg || !lbStage || lightboxState.scale <= 1) { lightboxState.tx = 0; lightboxState.ty = 0; return; }
    const rect = lbImg.getBoundingClientRect();
    const scaledW = rect.width, scaledH = rect.height;
    const stageW = lbStage.clientWidth, stageH = lbStage.clientHeight;
    const maxX = Math.max(0, (scaledW - stageW) / 2);
    const maxY = Math.max(0, (scaledH - stageH) / 2);
    lightboxState.tx = Math.max(-maxX, Math.min(maxX, lightboxState.tx));
    lightboxState.ty = Math.max(-maxY, Math.min(maxY, lightboxState.ty));
  }
  if (lightbox && lbImg && lbStage) {
    lbImg.addEventListener('click', (e) => {
      e.stopPropagation();
      if (lightboxState.scale > 1) resetZoom();
      else zoomBy(1.5, e.clientX, e.clientY);
    });
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox || e.target === lbStage) closeLightbox();
    });
    if (lbClose) lbClose.addEventListener('click', closeLightbox);
    if (lbZoomIn) lbZoomIn.addEventListener('click', () => zoomBy(ZOOM_STEP));
    if (lbZoomOut) lbZoomOut.addEventListener('click', () => zoomBy(-ZOOM_STEP));
    if (lbZoomReset) lbZoomReset.addEventListener('click', resetZoom);
    if (lbPrev) lbPrev.addEventListener('click', () => navigateLightbox(-1));
    if (lbNext) lbNext.addEventListener('click', () => navigateLightbox(1));
    if (lbAction) lbAction.addEventListener('click', () => {
      if (lightboxState.currentId) toggleSelect(lightboxState.currentId);
    });
    lbStage.addEventListener('wheel', (e) => {
      if (!lightboxState.open) return;
      e.preventDefault();
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      zoomBy(delta, e.clientX, e.clientY);
    }, { passive: false });
    lbImg.addEventListener('mousedown', (e) => {
      if (lightboxState.scale <= 1) return;
      e.preventDefault();
      lightboxState.isPanning = true;
      lightboxState.panStartX = e.clientX; lightboxState.panStartY = e.clientY;
      lightboxState.panOriginTx = lightboxState.tx; lightboxState.panOriginTy = lightboxState.ty;
      lightbox.classList.add('is-panning');
    });
    window.addEventListener('mousemove', (e) => {
      if (!lightboxState.isPanning) return;
      lightboxState.tx = lightboxState.panOriginTx + (e.clientX - lightboxState.panStartX);
      lightboxState.ty = lightboxState.panOriginTy + (e.clientY - lightboxState.panStartY);
      constrainPan();
      applyZoom();
    });
    window.addEventListener('mouseup', () => {
      if (lightboxState.isPanning) { lightboxState.isPanning = false; lightbox.classList.remove('is-panning'); }
    });

    let lastTouchEnd = 0;
    lbStage.addEventListener('touchstart', (e) => {
      if (!lightboxState.open) return;
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lightboxState.pinchStartDist = Math.hypot(dx, dy);
        lightboxState.pinchStartScale = lightboxState.scale;
      } else if (e.touches.length === 1) {
        if (lightboxState.scale > 1) {
          lightboxState.isPanning = true;
          lightboxState.panStartX = e.touches[0].clientX;
          lightboxState.panStartY = e.touches[0].clientY;
          lightboxState.panOriginTx = lightboxState.tx;
          lightboxState.panOriginTy = lightboxState.ty;
        } else {
          lightboxState.swipeActive = true;
          lightboxState.swipeStartX = e.touches[0].clientX;
          lightboxState.swipeStartY = e.touches[0].clientY;
        }
      }
    }, { passive: true });
    lbStage.addEventListener('touchmove', (e) => {
      if (!lightboxState.open) return;
      if (e.touches.length === 2) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        if (lightboxState.pinchStartDist > 0) {
          const ratio = dist / lightboxState.pinchStartDist;
          const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, lightboxState.pinchStartScale * ratio));
          lightboxState.scale = newScale;
          if (newScale === 1) { lightboxState.tx = 0; lightboxState.ty = 0; }
          applyZoom();
        }
      } else if (e.touches.length === 1 && lightboxState.isPanning) {
        e.preventDefault();
        lightboxState.tx = lightboxState.panOriginTx + (e.touches[0].clientX - lightboxState.panStartX);
        lightboxState.ty = lightboxState.panOriginTy + (e.touches[0].clientY - lightboxState.panStartY);
        constrainPan(); applyZoom();
      }
    }, { passive: false });
    lbStage.addEventListener('touchend', (e) => {
      if (!lightboxState.open) return;
      const now = Date.now();
      if (now - lastTouchEnd < 300 && e.changedTouches.length === 1 && !lightboxState.isPanning) {
        const t = e.changedTouches[0];
        if (lightboxState.scale > 1) resetZoom();
        else zoomBy(1.5, t.clientX, t.clientY);
      }
      lastTouchEnd = now;
      if (lightboxState.swipeActive && !lightboxState.isPanning) {
        const dx = e.changedTouches[0].clientX - lightboxState.swipeStartX;
        const dy = e.changedTouches[0].clientY - lightboxState.swipeStartY;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) navigateLightbox(dx < 0 ? 1 : -1);
      }
      lightboxState.isPanning = false;
      lightboxState.swipeActive = false;
      lightboxState.pinchStartDist = 0;
    });
    document.addEventListener('keydown', (e) => {
      if (!lightboxState.open) return;
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') navigateLightbox(-1);
      else if (e.key === 'ArrowRight') navigateLightbox(1);
      else if (e.key === '+' || e.key === '=') zoomBy(ZOOM_STEP);
      else if (e.key === '-' || e.key === '_') zoomBy(-ZOOM_STEP);
      else if (e.key === '0') resetZoom();
    });
  }

  /* ============================================================
     CHECKOUT (split free vs paid into different variants)
     ------------------------------------------------------------
     Each selected stain is added as its own cart line with
     visible line item properties (Stain / Wood Species / Sample).
     As a backup, the full list is also saved as a cart attribute
     ("Stain Samples") and in the order note, so the stains are
     visible on the order even if a theme/app strips properties.
     ============================================================ */
  const checkoutBtn = root.querySelector('[data-checkout]');
  if (checkoutBtn) {
    const label = checkoutBtn.querySelector('.ss-checkout-btn__label');
    const originalLabel = label ? label.textContent : '';
    const JSON_HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    const NOTE_MARKER = 'STAIN SAMPLES REQUESTED:';

    const showError = (msg, ms = 2500) => {
      if (label) {
        label.textContent = msg;
        setTimeout(() => { label.textContent = originalLabel; }, ms);
      }
    };

    const resetButton = () => {
      checkoutBtn.classList.remove('is-loading');
      checkoutBtn.disabled = false;
      if (label) label.textContent = originalLabel;
    };

    checkoutBtn.addEventListener('click', async () => {
      if (state.selected.size === 0) {
        showError('Please select at least one sample');
        return;
      }
      if (!FREE_VARIANT_ID) {
        showError('Free Sample product not configured', 3500);
        console.error('[stain-samples] No FREE variant ID. Set the "Free Sample Product" in section settings.');
        return;
      }

      // Resolve selected IDs to full stain objects (skip anything stale)
      const picks = [...state.selected]
        .map(id => STAINS.find(x => String(x.id) === String(id)))
        .filter(Boolean);

      if (picks.length === 0) {
        showError('Please select at least one sample');
        return;
      }
      if (picks.length > FREE_LIMIT && !PAID_VARIANT_ID) {
        showError('Paid Sample product not configured', 3500);
        console.error('[stain-samples] More than the free limit selected, but no PAID variant ID set.');
        return;
      }

      checkoutBtn.classList.add('is-loading');
      checkoutBtn.disabled = true;
      if (label) label.textContent = 'Adding to cart…';

      // One line per stain. Different properties keep the lines separate,
      // so 3 picks = 3 lines, each labelled with its stain.
      const items = picks.map((s, index) => {
        const isFree = index < FREE_LIMIT;
        return {
          id: Number(isFree ? FREE_VARIANT_ID : PAID_VARIANT_ID),
          quantity: 1,
          properties: {
            'Stain': s.name,
            'Wood Species': s.species,
            'Sample': `${index + 1} of ${picks.length} (${isFree ? 'free' : 'paid'})`,
          },
        };
      });

      // Human-readable summary used as a backup on the order
      const summary = picks
        .map((s, i) => `${i + 1}. ${s.name} — ${s.species}${i < FREE_LIMIT ? '' : ' (paid)'}`)
        .join('\n');

      try {
        // 1) Remove any sample lines left over from an earlier attempt
        let existingNote = '';
        try {
          const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
          if (cartRes.ok) {
            const cart = await cartRes.json();
            existingNote = cart.note || '';
            const updates = {};
            cart.items.forEach(item => {
              if (String(item.variant_id) === String(FREE_VARIANT_ID) ||
                  String(item.variant_id) === String(PAID_VARIANT_ID)) {
                updates[item.key] = 0;
              }
            });
            if (Object.keys(updates).length > 0) {
              await fetch('/cart/update.js', {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({ updates }),
              });
            }
          }
        } catch (e) {
          console.warn('[stain-samples] Could not pre-clean cart:', e);
        }

        // 2) Add the samples with their stain details
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Could not add samples to cart');
        }

        // Sanity check: warn if something stripped the properties
        const added = await res.json().catch(() => ({}));
        const addedItems = Array.isArray(added.items) ? added.items : [];
        if (addedItems.some(it => !it.properties || !it.properties['Stain'])) {
          console.warn('[stain-samples] Line item properties were not saved on some lines. ' +
            'A theme script or cart app may be re-adding items without properties. ' +
            'The cart attribute + note backup below will still record the stains.');
        }

        // 3) Backup: save the list as a cart attribute and in the order note
        //    (replaces any previous stain list, keeps the customer's own note text)
        const cleanedNote = existingNote.split(NOTE_MARKER)[0].trim();
        const newNote = (cleanedNote ? cleanedNote + '\n\n' : '') + NOTE_MARKER + '\n' + summary;
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify({
              note: newNote,
              attributes: { 'Stain Samples': summary },
            }),
          });
        } catch (e) {
          console.warn('[stain-samples] Could not save stain summary to cart:', e);
        }

        window.location.href = '/checkout';
      } catch (err) {
        console.error('[stain-samples]', err);
        resetButton();
        showError(String(err.message).slice(0, 60), 3500);
      }
    });
  }

  /* ----- init ----- */
  renderTabs();
  renderAll();
})();